import styled from '@emotion/styled';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Container } from '@mui/material';
import { CarouselBannersWithFallback } from '@web/common/components/banner/carousel-banners-with-fallback';
import { GenericSectionContainer } from '@web/common/components/generic-section-container';
import { Breadcrumb, BreadcrumbData } from '@web/common/components/microdata/breadcrumb-data';
import { SEO } from '@web/common/components/seo/seo';
import { SortDrawer } from '@web/common/components/sort-drawer';
import { VEROO_ORIGIN } from '@web/common/constants/url';
import { useSaveMeliuzCashbackCode } from '@web/common/hooks/use-save-meliuz-cashback-code';
import { useSavePromocode } from '@web/common/hooks/use-save-promocode';
import { useUtmLeadEventCreate } from '@web/common/hooks/use-utm-lead-event-create';
import { StyledContainerWithSections } from '@web/common/layout/styled-container-with-sections';
import { captureException } from '@web/common/sentry';
import { withApolloSSR } from '@web/common/with-apollo-ssr';
import { IndexLayout } from '@web/home/layout';
import { MobileProductKindSelector } from '@web/store/components/mobile-product-kind-selector';
import { ProductKindFilter } from '@web/store/components/product/product-kinds-filter';
import { StoreProductsGrid } from '@web/store/components/store-products-grid';
import { getProductKindOptionsDrawer } from '@web/store/get-product-kind-options-drawer';
import { formatProductToItem } from '@web/store/map-product-to-items';
import { productKindToQueryDict } from '@web/store/store-product-options';
import {
  ProductListFieldsFragment,
  ProductListImagesFieldsFragment,
  ProductsDocument,
  ProductsQuery,
  ProductsQueryVariables,
  useProductsQuery,
} from 'core/lib/graphql/store/_gen/products';
import { BannerKind, FinalityKind, ProductKind } from 'core/lib/graphql/types.d';
import {
  BannersDocument,
  BannersQuery,
  BannersQueryVariables,
  useBannersQuery,
} from 'core/lib/graphql/_gen/banners';
import { useMeQuery } from 'core/lib/graphql/_gen/me';
import { GetServerSideProps } from 'next';
import { FC, useCallback, useMemo, useState } from 'react';

const StyledSortDrawer = styled(SortDrawer)`
  ${props => props.theme.breakpoints.up('md')} {
    display: none;
  }
`;

type StoreDrawer = 'productKinds';

const onSaleOrderBy = ['-onSaleDiscount', '-createdAt'];
const productsOrderBy = ['-isFeatured', '-createdAt'];

const kindOptions = getProductKindOptionsDrawer();

const StoreIndex: FC = () => {
  const [openDrawer, setOpenDrawer] = useState<StoreDrawer | null>(null);

  // GraphQL
  const meQ = useMeQuery();
  const bannersQ = useBannersQuery({
    variables: {
      kind: BannerKind.ECommerce,
    },
  });
  const onSaleProductsQ = useProductsQuery({
    variables: {
      finality: FinalityKind.Ecommerce,
      first: 5,
      orderby: onSaleOrderBy,
      isOnSale: true,
    },
  });
  const coffeeProductsQ = useProductsQuery({
    variables: {
      finality: FinalityKind.Ecommerce,
      kinds: ProductKind.Coffee,
      first: 9,
      orderby: productsOrderBy,
    },
  });
  const utensilProductsQ = useProductsQuery({
    variables: {
      finality: FinalityKind.Ecommerce,
      kinds: ProductKind.Utensil,
      first: 4,
      orderby: productsOrderBy,
    },
  });
  const accessoryProductsQ = useProductsQuery({
    variables: {
      finality: FinalityKind.Ecommerce,
      kinds: ProductKind.Accessory,
      first: 4,
      orderby: productsOrderBy,
    },
  });
  const kitProductsQ = useProductsQuery({
    variables: {
      finality: FinalityKind.Ecommerce,
      kinds: ProductKind.Kit,
      first: 4,
      orderby: productsOrderBy,
    },
  });

  const formatProductToItemFn = useCallback(
    (node: ProductListFieldsFragment & ProductListImagesFieldsFragment) =>
      formatProductToItem(node, meQ.data?.me?.isSubscriber),
    [meQ.data?.me],
  );

  const onSaleProducts = useMemo(
    () => (onSaleProductsQ.data?.products?.edges || []).map(it => formatProductToItemFn(it!.node!)),
    [formatProductToItemFn, onSaleProductsQ.data?.products?.edges],
  );
  const coffeeProducts = useMemo(
    () => (coffeeProductsQ.data?.products?.edges || []).map(it => formatProductToItemFn(it!.node!)),
    [formatProductToItemFn, coffeeProductsQ.data],
  );
  const utensilProducts = useMemo(
    () =>
      (utensilProductsQ.data?.products?.edges || []).map(it => formatProductToItemFn(it!.node!)),
    [formatProductToItemFn, utensilProductsQ.data],
  );
  const accessoryProducts = useMemo(
    () =>
      (accessoryProductsQ.data?.products?.edges || []).map(it => formatProductToItemFn(it!.node!)),
    [formatProductToItemFn, accessoryProductsQ.data],
  );
  const kitProducts = useMemo(
    () => (kitProductsQ.data?.products?.edges || []).map(it => formatProductToItemFn(it!.node!)),
    [formatProductToItemFn, kitProductsQ.data],
  );
  const banners = useMemo(() => bannersQ.data?.banners || [], [bannersQ.data]);

  const storeBreadcrumb: Breadcrumb[] = useMemo(
    () => [
      {
        name: 'Veroo',
        url: `${VEROO_ORIGIN}`,
      },
      {
        name: 'Loja',
        url: `${VEROO_ORIGIN}/loja`,
      },
    ],
    [],
  );

  // Hooks
  useSavePromocode();
  useSaveMeliuzCashbackCode();
  useUtmLeadEventCreate({ finality: FinalityKind.Ecommerce });

  return (
    <IndexLayout
      bottomNavChildren={
        <MobileProductKindSelector openDrawer={() => setOpenDrawer('productKinds')} />
      }
    >
      <SEO title="Loja" />
      <StyledSortDrawer
        title="Selecione a categoria"
        anchor="bottom"
        menuItems={kindOptions}
        open={openDrawer === 'productKinds'}
        onClose={() => setOpenDrawer(null)}
        onCloseIcon={<KeyboardArrowDownIcon fontSize="large" />}
      />
      <Container fixed>
        <ProductKindFilter />
      </Container>
      <StyledContainerWithSections fixed>
        <CarouselBannersWithFallback banners={banners} />
        {onSaleProducts.length > 0 && (
          <GenericSectionContainer
            title="Ofertas"
            subtitle="Cafés especiais com aquele desconto que todo mundo gosta, aproveite!"
            disableGutters
            data-e2e="store-generic-section-offers"
          >
            <StoreProductsGrid items={onSaleProducts} />
          </GenericSectionContainer>
        )}
        {coffeeProducts.length > 0 && (
          <GenericSectionContainer
            title="Cafés"
            disableGutters
            data-e2e="store-generic-section-coffees"
          >
            <StoreProductsGrid
              items={coffeeProducts}
              moreButton={
                (coffeeProductsQ.data?.products?.totalCount || 0) > 9
                  ? { url: `/loja/categoria/${productKindToQueryDict.COFFEE}` }
                  : undefined
              }
            />
          </GenericSectionContainer>
        )}
        {utensilProducts.length > 0 && (
          <GenericSectionContainer
            title="Utensílios"
            disableGutters
            data-e2e="store-generic-section-utensils"
          >
            <StoreProductsGrid
              items={utensilProducts}
              horizontalBreakpoint="sm"
              moreButton={
                (utensilProductsQ.data?.products?.totalCount || 0) > 4
                  ? { url: `/loja/categoria/${productKindToQueryDict.UTENSIL}` }
                  : undefined
              }
            />
          </GenericSectionContainer>
        )}
        {kitProducts.length > 0 && (
          <GenericSectionContainer
            title="Kits"
            disableGutters
            data-e2e="store-generic-section-kits"
          >
            <StoreProductsGrid
              items={kitProducts}
              horizontalBreakpoint="sm"
              moreButton={
                (kitProductsQ.data?.products?.totalCount || 0) > 4
                  ? { url: `/loja/categoria/${productKindToQueryDict.KIT}` }
                  : undefined
              }
            />
          </GenericSectionContainer>
        )}
        {accessoryProducts.length > 0 && (
          <GenericSectionContainer
            title="Harmonizações"
            disableGutters
            data-e2e="store-generic-section-food-pairings"
          >
            <StoreProductsGrid
              items={accessoryProducts}
              horizontalBreakpoint="sm"
              moreButton={
                (accessoryProductsQ.data?.products?.totalCount || 0) > 4
                  ? { url: `/loja/categoria/${productKindToQueryDict.ACCESSORY}` }
                  : undefined
              }
            />
          </GenericSectionContainer>
        )}
      </StyledContainerWithSections>
      <BreadcrumbData breadcrumbList={storeBreadcrumb} />
    </IndexLayout>
  );
};

export const getServerSideProps: GetServerSideProps = withApolloSSR(async ({ apolloClient }) => {
  try {
    await Promise.all(
      (
        [
          apolloClient.query<BannersQuery, BannersQueryVariables>({
            query: BannersDocument,
            variables: { kind: BannerKind.ECommerce },
          }),
        ] as any
      ).concat(
        [
          { isOnSale: true },
          { first: 9, kinds: ProductKind.Coffee },
          { kinds: ProductKind.Utensil },
          { kinds: ProductKind.Kit },
        ].map(it =>
          apolloClient.query<ProductsQuery, ProductsQueryVariables>({
            query: ProductsDocument,
            variables: {
              finality: FinalityKind.Ecommerce,
              first: 4,
              orderby: productsOrderBy,
              ...it,
            },
          }),
        ),
      ),
    );
  } catch (e) {
    captureException(e);
  }
  return { props: {} };
});

export default StoreIndex;
